import { formatNextDue, type PortalViewOutput } from '@garage/shared';
import { useMutation } from '@tanstack/react-query';
import { downloadRoute } from '../../garage/lib/download.js';
import { Button } from '../../ui/Button.js';
import { cx } from '../../ui/cx.js';
import { usePortalPreview } from '../preview.js';
import { getPortalToken } from '../token.js';
import { Block, CARD } from './portal-ui.js';

/**
 * Compte rendu de revente (08/10/2026), mode `report` seulement : préconisations et prochaine échéance du
 * garage, puis téléchargement du PDF à la marque du garage (`portal.releaseReportPdf`, jeton en en-tête,
 * jamais dans l'adresse ; sans montants). Aperçu côté garage : pas de téléchargement (aucun jeton).
 */
export function ReleaseReportBlock({ view }: { view: PortalViewOutput }) {
  const preview = usePortalPreview() !== null;
  const pdf = useMutation({
    mutationFn: () => downloadRoute('portal.releaseReportPdf', undefined, { fallbackName: 'compte-rendu.pdf', portalToken: getPortalToken() }),
  });
  if (view.mode !== 'report') return null;
  const it = view.intervention;
  const recommendations = it.recommendations?.trim() || null;
  const nextDue = formatNextDue(it.nextDue);
  return (
    <>
      {recommendations || nextDue ? (
        <Block title="Préconisations du garage">
          <div className={cx(CARD, 'space-y-3')}>
            {recommendations ? <p className="whitespace-pre-line text-base text-ink">{recommendations}</p> : null}
            {nextDue ? (
              <p className="rounded-xl bg-sunken p-3.5 text-base font-medium text-ink" data-testid="next-due">
                {nextDue}
              </p>
            ) : null}
          </div>
        </Block>
      ) : null}
      <Block title="Compte rendu à conserver">
        <div className={cx(CARD, 'space-y-3')}>
          <p className="text-base text-ink-2">
            Le compte rendu de l’intervention, établi par {view.garage.displayName} : véhicule, kilométrages, travaux réalisés, préconisations et photos. Utile à
            conserver, et à remettre en cas de revente.
          </p>
          {preview ? null : (
            <Button variant="secondary" block className="sm:w-auto" loading={pdf.isPending} onClick={() => pdf.mutate()}>
              Télécharger le compte rendu (PDF)
            </Button>
          )}
          {pdf.error ? (
            <p role="alert" className="rounded-xl border border-danger-line bg-danger-bg p-3.5 text-danger-fg">
              Le téléchargement n’a pas abouti. Vérifiez votre connexion et réessayez.
            </p>
          ) : null}
        </div>
      </Block>
    </>
  );
}
